import React, { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { EmojiButton } from '@joeattardi/emoji-button';

// ─── Recently Used (localStorage) ───────────────────────────────────────────
const RECENTS_KEY = 'notesplus_emoji_recents';
const MAX_RECENTS = 20;

export function saveRecentEmoji(emoji) {
  try {
    const existing = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
    const filtered = existing.filter((e) => e !== emoji);
    const updated = [emoji, ...filtered].slice(0, MAX_RECENTS);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
  } catch (_) {}
}

// ─── Shared Picker Options ───────────────────────────────────────────────────
// style: 'twemoji' enables the library's built-in Twemoji rendering for all
// emoji buttons inside the picker — they become <img> tags instead of system glyphs.
const getPickerOptions = (t) => ({
  theme: 'dark',
  style: 'twemoji',                           // ← Twemoji rendering inside picker
  twemojiOptions: {                           // ← passed directly to twemoji.parse()
    base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/',
    size: '72x72',
    ext: '.png',
    className: 'twemoji',
  },
  showSearch: true,
  showCategoryButtons: true,
  showRecents: true,
  recentsCount: MAX_RECENTS,
  i18n: {
    search: t('emojiPicker.search'),
    categories: {
      recents: t('emojiPicker.categories.recents'),
      smileys: t('emojiPicker.categories.smileys'),
      people: t('emojiPicker.categories.people'),
      animals: t('emojiPicker.categories.animals'),
      food: t('emojiPicker.categories.food'),
      activities: t('emojiPicker.categories.activities'),
      travel: t('emojiPicker.categories.travel'),
      objects: t('emojiPicker.categories.objects'),
      symbols: t('emojiPicker.categories.symbols'),
      flags: t('emojiPicker.categories.flags'),
    },
    notFound: t('emojiPicker.notFound'),
  },
  styleProperties: {
    '--background-color': '#0d1424',
    '--border-color': 'rgba(255,255,255,0.08)',
    '--text-color': '#c8d8eb',
    '--secondary-text-color': '#4d6a8a',
    '--hover-color': '#1e2d47',
    '--focus-indicator-color': '#3d8ef0',
    '--search-background-color': '#161f31',
    '--search-border-color': 'rgba(255,255,255,0.08)',
    '--search-placeholder-color': '#7a96b0',
    '--category-button-color': '#7a96b0',
    '--category-button-active-color': '#3d8ef0',
    '--tag-background-color': '#1a2640',
  },
});

// ─── EmojiPickerButton ───────────────────────────────────────────────────────
// Main emoji button — attach to the message input area.
// The picker renders all emojis as Twemoji <img> tags (cross-platform consistency).
// Children: custom button content; defaults to the smiley face SVG.
const EmojiPickerButton = ({
  onEmojiSelect,
  disabled = false,
  className = '',
  position = 'top-start',
  children,
}) => {
  const { t } = useTranslation('chat');
  const buttonRef = useRef(null);
  const pickerRef = useRef(null);
  const onSelectRef = useRef(onEmojiSelect);

  // Keep callback ref fresh without recreating the picker
  useEffect(() => {
    onSelectRef.current = onEmojiSelect;
  }, [onEmojiSelect]);

  useEffect(() => {
    const picker = new EmojiButton({ ...getPickerOptions(t), position });

    picker.on('emoji', (selection) => {
      onSelectRef.current?.(selection.emoji);
      saveRecentEmoji(selection.emoji);
    });

    pickerRef.current = picker;

    return () => {
      pickerRef.current?.destroyPicker?.();
      pickerRef.current = null;
    };
  }, [position, t]);

  const handleClick = useCallback(() => {
    if (pickerRef.current && buttonRef.current) {
      pickerRef.current.togglePicker(buttonRef.current);
    }
  }, []);

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={t('emojiPicker.buttonTitle')}
      className={`transition-all duration-200 ${
        disabled ? 'opacity-50 pointer-events-none' : ''
      } ${className}`}
    >
      {children ?? (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      )}
    </button>
  );
};

// ─── ReactionEmojiPicker ─────────────────────────────────────────────────────
// Opens the full emoji picker at a specific (x, y) screen position.
// All emojis inside are rendered as Twemoji images for cross-platform consistency.
export const ReactionEmojiPicker = ({ x, y, messageId, onSelect, onClose }) => {
  const { t } = useTranslation('chat');
  const anchorRef = useRef(null);
  const pickerRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);

  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!anchorRef.current) return;

    const picker = new EmojiButton({
      ...getPickerOptions(t),
      position: 'bottom-start',
    });

    picker.on('emoji', (selection) => {
      onSelectRef.current?.(selection.emoji, messageId);
      saveRecentEmoji(selection.emoji);
    });

    picker.on('hidden', () => {
      onCloseRef.current?.();
    });

    pickerRef.current = picker;
    picker.showPicker(anchorRef.current);

    return () => {
      pickerRef.current?.destroyPicker?.();
      pickerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, x, y]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={anchorRef}
      style={{
        position: 'fixed',
        left: x,
        top: y,
        width: 0,
        height: 0,
        pointerEvents: 'none',
      }}
    />,
    document.body
  );
};

export default EmojiPickerButton;
